import { CM_IN_ONE_INCHES } from '../config/constant/unityConstants';
import {
  CONVERT_TO_CM,
  DECIMAL_NUM_COUNT,
  MAX_RESOLUTION,
  MEASURE_UNIT_LABEL,
  MIN_RESOLUTION,
  REVERSE_PROPERTY_MAP,
  TILING_KEY,
} from '../config/constant/unityConstants';
import { ADMIN_DASHBOARD } from '../constants/routePathConstants';
import { MAX_UPLOAD_HEIGHT_PIXELS, MAX_UPLOAD_WIDTH_PIXELS } from '../constants/textureConstants';
import { getCurrentUserOrganization } from '../contexts/UserContext';
import { selectColor } from '../redux/slicers/admin/curatorStylesSlicer';
import { selectCanvasSize } from '../redux/slicers/camera/cameraSettings';
import { selectGlobalRenderingInfoData } from '../redux/slicers/globalRenderingInfoSlicer';
import { getAppState } from '../redux/store';
import { getJson } from './axiosHelper';
import { decimals, getImageDetails } from './jsHelper';
import { errorToast, warningToast } from './toastHelper';
import { getProjectsPath } from './urlHelper';

// Unity tilling conversion
// when we send unit to unity then we do 10 divide by feet always
export const cmToUnitySize = (cm) => {
  // new formula 120/inches

  if (cm === 0) return 0;

  const feet = cm / CONVERT_TO_CM;
  return 10 / feet;
};

// when we convert unity size to active unit then we do 10 divide by unity size always
export const unitySizeToCM = (unitySize, count = DECIMAL_NUM_COUNT) => {
  // return 120 / unitySize

  const feet = 10 / unitySize;
  return parseFloat((feet * CONVERT_TO_CM).toFixed(count));
};

export const sizeDecimals = (size) => parseFloat(size.toFixed(2));
export const toUnityFixed = (size, decimals = 2) => parseFloat(parseFloat(size).toFixed(decimals));
export const parseELSize = (size) => {
  const floatSize = parseFloat(size);
  if (Number.isNaN(floatSize)) return floatSize;

  return parseFloat(floatSize.toFixed(2));
};

export const convertBytesInMB = (size = 0) => (size ? sizeDecimals(size * 0.000001) : size);

window.unitySizeToCM = unitySizeToCM;
window.cmToUnitySize = cmToUnitySize;

export const normalizeResSize = (size) => {
  const maxRes = getMaxResolution(true);
  return size > maxRes ? maxRes : size < MIN_RESOLUTION ? MIN_RESOLUTION : size;
};

const lightDataCache = {};
export const getPropLightList = async (prop) => {
  let lightArr = [];
  if (prop.is_light_available && prop.light_data) {
    let lightData = lightDataCache[prop.id] || (await getJson(prop.light_data));
    lightDataCache[prop.id] = lightData;
    lightArr = lightData?.lights || [];
  }

  return lightArr;
};

export const getPropMetadata = (prop) => {
  return prop.is_metadata && prop.product_meta_info
    ? { preDefinedMetadata: prop.product_meta_info }
    : {};
};

export const checkIsTextureExpiredOrNot = (texture, showMessage = true) => {
  if (texture?.is_special_case_texture && !texture?.is_archived) return false;
  const isMattressTexture = texture?.is_mattress_texture;
  const isMattressTextureExpired = texture?.is_mattress_expired;
  const isCustomerProductExpired = texture?.is_customer_product_expired;
  const isExpired = isMattressTexture ? isMattressTextureExpired : isCustomerProductExpired;
  if (!isExpired) return false;
  showMessage && warningToast('Texture expired. Contact support or account admin for help.');
  return true;
};

export const prepareTextureForUpload = async ({
  file,
  fileName,
  isMap = false,
  projectId,
  filteroptions,
  name,
  oldTextureId,
  version,
}) => {
  const formData = new FormData();
  formData.append('texture_file', file, fileName || file.name);
  formData.append('is_map', isMap);

  if (projectId) {
    formData.append('project', projectId);
  }

  if (oldTextureId) {
    formData.append('old_texture_id', oldTextureId);
  }

  if (version) {
    formData.append('version', version);
  }

  if (name) {
    formData.append('name', name);
  }

  if (filteroptions) {
    formData.append('filteroptions', filteroptions);
  }

  try {
    const { width, height, dpi } = await getImageDetails(file);

    if (width > MAX_UPLOAD_WIDTH_PIXELS || height > MAX_UPLOAD_HEIGHT_PIXELS) {
      errorToast('The texture you have uploaded is above 16k resolution');
      return;
    }

    formData.append('width', toUnityFixed(width / dpi) * CM_IN_ONE_INCHES);
    formData.append('height', toUnityFixed(height / dpi) * CM_IN_ONE_INCHES);
  } catch (error) {
    console.error(error);
    errorToast('Unable to get image details, please try again');
    return;
  }

  return {
    formData,
  };
};

export const getTextureSize = async (texture, textureUrl) => {
  if (!texture && !textureUrl) return;

  let textureWidth = parseFloat(texture.width);
  let textureHeight = parseFloat(texture.height);

  if (!textureWidth || !textureHeight) {
    const url = textureUrl || texture.texture_file || texture.low_texture_file;
    const { widthInches, heightInches } = await getImageDetails(url);
    textureWidth = widthInches;
    textureHeight = heightInches;
  }

  return { textureWidth, textureHeight };
};

export const combineUnityObjectDataWithReact = (
  rawObject,
  parsedObject,
  { applySelectedColor } = {}
) => {
  if (!parsedObject) return rawObject;

  const tempRawObject = {
    ...rawObject,
    tilingOffsetValue: {
      ...rawObject.tilingOffsetValue,
    },
  };

  const syncTilingValues = (key, tiling) => {
    if (!tiling) return;

    tempRawObject.tilingOffsetValue[key] = {
      ...tempRawObject.tilingOffsetValue[key],
      xMatTiling: cmToUnitySize(tiling.width),
      yMatTiling: cmToUnitySize(tiling.height),
      xMatOffset: tiling.x,
      yMatOffset: tiling.y,
      rotation: tiling.rotation,
    };
  };

  // raw object is out of date so we need to sync it with latest data
  Object.keys(tempRawObject.tilingOffsetValue).forEach((key) => {
    const parsedObjectKey = REVERSE_PROPERTY_MAP[key];
    const tiling = parsedObject[parsedObjectKey];
    syncTilingValues(key, tiling);
  });

  const materialKey = getMaterialTilingKey(tempRawObject);
  if (materialKey) {
    // add material data
    syncTilingValues(materialKey, parsedObject.material);
  }

  if (applySelectedColor) {
    const selectedColor = selectColor(getAppState());
    const color = typeof selectedColor === 'string' ? selectedColor : selectedColor?.hex;
    if (color) {
      tempRawObject.colorValue = color;
    }
  }

  // sync intensity
  tempRawObject.normalIntensity = parsedObject.normal.intensity;
  tempRawObject.roughnessIntensity = parsedObject.glossiness.intensity;
  tempRawObject.aoIntensity = parsedObject.ao.intensity;
  tempRawObject.emissionIntensity = parsedObject.emission.intensity;
  tempRawObject.metalIntensity = parsedObject.metallic.intensity;
  tempRawObject.transparencyIntensity = parsedObject.transparency.intensity;
  tempRawObject.effectLightColorBounce = parsedObject.other.effectLightColorBounce;

  return tempRawObject;
};

export function getMaterialTilingKey(data) {
  const { materialTilingKeys } = data;
  let key = TILING_KEY._MainTex;

  if (materialTilingKeys.includes(TILING_KEY._MainTex)) {
    key = TILING_KEY._MainTex;
  } else if (materialTilingKeys.includes(TILING_KEY._BumpMap)) {
    key = TILING_KEY._BumpMap;
  } else if (materialTilingKeys.includes(TILING_KEY._SmoothnessMap)) {
    key = TILING_KEY._SmoothnessMap;
  } else if (materialTilingKeys.includes(TILING_KEY._OcclusionMap)) {
    key = TILING_KEY._OcclusionMap;
  } else if (materialTilingKeys.includes(TILING_KEY._MetallicGlossMap)) {
    key = TILING_KEY._MetallicGlossMap;
  } else if (materialTilingKeys.includes(TILING_KEY._AlphaTexture)) {
    key = TILING_KEY._AlphaTexture;
  }
  return key;
}

export const isExclusiveProp = (prop) => {
  return !prop.assign_to_all_org; // && prop.owner_organization_details?.id === getCurrentUserOrganization();
};

export const getTextureFormat = (texture) => {
  const textureUrl = texture?.presigned_url || texture.texture_file || texture.low_texture_file;
  if (typeof textureUrl !== 'string') return;

  try {
    const url = new URL(textureUrl);
    const isPng = url.pathname.endsWith('.png');

    return isPng ? 'png' : 'jpg';
  } catch (e) {
    console.error(e);
    return;
  }
};

export const forceCloseCurator = ({ url } = {}) => {
  window.close();
  setTimeout(() => {
    window.location.assign(url || getProjectsPath()); // redirect to proejcts if window close doesn't work
  }, 50);
};

export const forceCloseTab = () => {
  window.close();
  setTimeout(() => {
    window.location.assign(ADMIN_DASHBOARD);
  }, 50);
};

export const calculateSafeFrameSize = ({ cameraWidth, cameraHeight }) => {
  const { width: canvasWidth, height: canvasHeight } = selectCanvasSize(getAppState());

  const canvasRatio = canvasWidth / canvasHeight;
  const cameraRatio = cameraWidth / cameraHeight;

  if (cameraRatio === canvasRatio) {
    return {
      height: decimals(canvasWidth, 4), // TODO: shouldn't here be height??? check it when you have time
      width: decimals(canvasWidth, 4),
    };
  } else if (cameraRatio > canvasRatio) {
    const height = cameraHeight * (canvasWidth / cameraWidth);
    return {
      height: decimals(height, 4),
      width: decimals(canvasWidth, 4),
    };
  } else if (cameraRatio < canvasRatio) {
    const width = cameraWidth * (canvasHeight / cameraHeight);

    return {
      height: decimals(canvasHeight, 4),
      width: decimals(width, 4),
    };
  }
};

export const getMeasureUnityLabel = (measureUnits) => {
  return MEASURE_UNIT_LABEL[measureUnits] || '';
};

export const getMaxResolution = (returnOne = false) => {
  const globalRenderingInfo = selectGlobalRenderingInfoData(getAppState());
  if (!globalRenderingInfo) {
    return !returnOne
      ? {
          maxXRes: MAX_RESOLUTION,
          maxYRes: MAX_RESOLUTION,
        }
      : MAX_RESOLUTION;
  }
  const {
    organization_camera_info,
    render_max_height_limit = 0,
    render_max_width_limit = 0,
  } = globalRenderingInfo;
  const maxOrgXRes = organization_camera_info?.render_max_width_limit || 0;
  const maxOrgYRes = organization_camera_info?.render_max_height_limit || 0;
  const maxGlobalXRes = render_max_width_limit || 0;
  const maxGlobalYRes = render_max_height_limit || 0;
  const maxXRes = maxOrgXRes ? maxOrgXRes : maxGlobalXRes ? maxGlobalXRes : MAX_RESOLUTION;
  const maxYRes = maxOrgYRes ? maxOrgYRes : maxGlobalYRes ? maxGlobalYRes : MAX_RESOLUTION;
  return !returnOne
    ? {
        maxXRes,
        maxYRes,
      }
    : Math.max(maxXRes, maxYRes);
};
